//@flow
import React, {useEffect} from 'react';
import {useFormContext} from 'react-hook-form';
import {
  Checkbox,
  FormControlLabel,
  makeStyles,
  Typography,
} from '@material-ui/core';
import {TRIGGER_FIELDS} from '../../../const';
import {FILTER_TYPES} from '../../../../../utils/triggerConfig';
import {
  FILTER_SET_VALUE_OPTIONS,
  setFilterFieldUpdates,
} from '../../../../../utils/filterFields';
import {
  useConversationTags,
  useSetInitialConversationTags,
} from '../../../../../../chat/utils/conversationTag';
import {getFormError} from '../../../../../utils/formErrors';
import {colors} from '../../../../../../../../assets/styles/colors';
import type {CrmTagsFilter as CrmTagsFilterValues} from '../../../../../../../../entities';

const useStyles = makeStyles(() => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    gap: '.5rem',
  },
  label: {
    fontSize: '.875rem',
    fontWeight: 600,
  },
  tagsList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '.25rem',
  },
  fieldError: {
    fontSize: '.8125rem',
    color: colors.danger,
  },
}));

type Props = {
  filterType: typeof FILTER_TYPES.CRM_TAGS,
};

export default function CrmTagsFilter({filterType}: Props) {
  const classes = useStyles();
  const {register, watch, setValue, getValues, errors} = useFormContext();

  useSetInitialConversationTags();
  const {conversationTags} = useConversationTags();

  const fieldPrefix = `${TRIGGER_FIELDS.filters}.${filterType}`;

  useEffect(() => {
    register(`${fieldPrefix}.tagIds`);
  }, [register, fieldPrefix]);

  const allFilters = watch(TRIGGER_FIELDS.filters) || {};
  const filter: CrmTagsFilterValues = allFilters[filterType] || {tagIds: []};
  const selectedTagIds = filter.tagIds || [];
  const tagsError = getFormError(errors, `${fieldPrefix}.tagIds`);

  const handleToggleTag = (tagId: string) => {
    const exists = selectedTagIds.includes(tagId);
    const nextTagIds = exists
      ? selectedTagIds.filter(id => id !== tagId)
      : [...selectedTagIds, tagId];

    setFilterFieldUpdates(
      getValues,
      setValue,
      filterType,
      {tagIds: nextTagIds},
      FILTER_SET_VALUE_OPTIONS
    );
  };

  return (
    <div className={classes.root}>
      <Typography className={classes.label}>Etiquetas</Typography>
      <div className={classes.tagsList}>
        {conversationTags.length === 0 && (
          <Typography variant="body2">No hay etiquetas disponibles</Typography>
        )}
        {conversationTags.map(tag => (
          <FormControlLabel
            key={tag.id}
            control={
              <Checkbox
                color="primary"
                checked={selectedTagIds.includes(tag.id)}
                onChange={() => handleToggleTag(tag.id)}
              />
            }
            label={tag.tag}
          />
        ))}
      </div>
      {tagsError && (
        <Typography className={classes.fieldError}>{tagsError}</Typography>
      )}
    </div>
  );
}
